import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthProvider";
import { useLanguage } from "../context/LanguageProvider";
import { BASE_URL } from '../apiConfig';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

export default function ConfirmatorExportExcel({ onClose }) {
  const { t } = useLanguage();
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: 'all'
  });

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/confirmators-invoices/export`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setInvoices(data.invoices);
      } else {
        toast.error(data.message || t('cors_confirmator_export.fetch_failed'));
      }
    } catch (err) {
      console.error(err);
      toast.error(t('cors_confirmator_export.error_occurred'));
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    let matches = true;
    
    if (filters.startDate) {
      matches = matches && new Date(invoice.created_at) >= new Date(filters.startDate);
    }
    
    if (filters.endDate) {
      matches = matches && new Date(invoice.created_at) <= new Date(filters.endDate + 'T23:59:59');
    }
    
    if (filters.status !== 'all') {
      matches = matches && invoice.status === filters.status;
    }
    
    return matches;
  });

  const exportToExcel = () => {
    const worksheetData = filteredInvoices.map(invoice => ({
      'Invoice Number': invoice.invoice_number,
      'Confirmator': invoice.confirmator?.name || 'N/A',
      'Email': invoice.confirmator?.email || 'N/A',
      'Total Amount (MAD)': parseFloat(invoice.total).toFixed(2),
      'Status': invoice.status,
      'Payment Method': invoice.payment_method || '-',
      'Bank': invoice.bank || '-',
      'Payment Date': invoice.payment_date ? new Date(invoice.payment_date).toLocaleDateString() : '-',
      'Created Date': new Date(invoice.created_at).toLocaleDateString(),
      'Number of Orders': invoice.orderItems?.length || 0
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, t('cors_confirmator_export.sheet_name'));
    
    const fileName = `confirmator_invoices_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    toast.success(t('cors_confirmator_export.download_success'));
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">{t('cors_confirmator_export.title')}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cors_confirmator_export.start_date')}</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cors_confirmator_export.end_date')}</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('cors_confirmator_export.status')}</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">{t('cors_confirmator_export.all_statuses')}</option>
              <option value="pending">{t('cors_confirmator_export.pending')}</option>
              <option value="paid">{t('cors_confirmator_export.paid')}</option>
              <option value="cancelled">{t('cors_confirmator_export.cancelled')}</option>
            </select>
          </div>
        </div>

        <div className="bg-gray-50 p-3 rounded-lg mb-4">
          <p className="text-sm text-gray-600">
            {filteredInvoices.length} {t('cors_confirmator_export.export_count')}
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('cors_confirmator_export.cancel')}
          </button>
          <button
            onClick={exportToExcel}
            disabled={loading || filteredInvoices.length === 0}
            className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? t('cors_confirmator_export.loading') : t('cors_confirmator_export.export')}
          </button>
        </div>
      </div>
    </div>
  );
}